import { getWork, getWorkForOwner, getWorkList, getWorkListForOwner, updateWork, updateWorkBulk, updateWorkBulkInfo, deleteWork, deleteWorksBulk, getSimpleWorkListForImportForOwner, getSimpleWorkListForExportForOwner, toggleWorkAllowDownload, toggleWorkAccess, getSelectedWorkListForOwner, getManualOrderWorkListForOwner, reorderWorks, updateWorkListType } from '../../api/workListApi';
import { useWorkListStore } from '../../store/WorkListStore';
import { useWorkViewStore } from '../../store/WorkViewStore';
import { IndexOrderWorkData, WorkData } from '../../dto/EntityRepository';
import { DeleteWorkReq, UpdateWorkReq, MetadataForQuery, UpdateWorkBulkReq, UpdateWorkBulkInfoReq, UpdateReorderListReq, ToggleWorkAccessReq, UpdateWorkListTypeReq } from '../../dto/ReqDtoRepository';
import { DeleteResponse, ToggleWorkAccessResponse, WorkPageResponse, WorkListResponse, WorkListTypeResponse, WorkManualOrderListResponse, WorkResponse, WorkSimplePageResponseForExport, WorkSimplePageResponseForImport, WorkWithDetailAndRelatedProjectResponse } from '../../dto/ResDtoRepository';
import { useImportStationWorkStore } from '../../store/importStationWorkStore';
import { useExportStationWorkStore } from '../../store/exportStationWorkStore';
import { useTempAlertStore } from '../../store/portal/tempAlertStore';
import { useApiWrapper } from './apiWrapper';
import { useModalPageStore, usePageStore } from '../../store/pageStore';
import { useModalStore } from '../../store/portal/modalStore';
import { WorkListType } from '../../enum/EnumRepository';
import { usePageOwnerIdentityStore } from '../../store/pageOwnerIdentityStore';
import { useEditModeStore } from '../../store/authStore';
import { convertWorkToSelectedData } from '../../converter/converter';
import { useWorkIndexStore } from '../../store/WorkIndexStore';
import {
  normalizeWorkData,
  normalizeWorkSimpleData,
  normalizeWorkSimpleForExportData,
} from '../../util/normalizeWorkData';
import { runCurrentArchiveRequest } from '../../util/currentArchiveRequest';
import type { ResourceScope, ResourceStatus } from '../../util/scopedResource';


interface UseWorkListStateResult {
  workList: WorkData[];
  workListType: WorkListType;
  listOwnerAui: string | null;
  listScope: ResourceScope | null;
  status: ResourceStatus;
}

interface UseWorkListActionsResult {
  getWork: (workId: string) => Promise<void>;
  getWorkOwner: (workId: string) => Promise<void>;
  getWorkList: (metaData: MetadataForQuery) => Promise<void>;
  getWorkListOwner: (metaData: MetadataForQuery) => Promise<void>;
  getSelectedWorkListForOwner: (workIds: string[]) => Promise<void>;
  /** 수동 순서(lex index)가 있는 작품만 — 인덱싱 패널 초기 목록 */
  getManualOrderWorkListForOwner: () => Promise<void>;
  getSelectedWorkListForExport: (workIds: string[]) => Promise<void>;
  getSimpleWorkListForImport: (metaData: MetadataForQuery) => Promise<void>;
  getSimpleWorkListForExport: (metaData: MetadataForQuery) => Promise<void>;
  updateWork: (data: UpdateWorkReq) => Promise<void>;
  updateWorkBulk: (data: UpdateWorkBulkReq) => Promise<void>;
  updateWorkBulkInfo: (data: UpdateWorkBulkInfoReq) => Promise<void>;
  reorderWorks: (data: UpdateReorderListReq) => Promise<void>;
  updateWorkListType: (data: UpdateWorkListTypeReq) => Promise<void>;
  toggleWorkAllowDownload: (data: UpdateWorkReq) => Promise<void>;
  toggleWorkAccess: (data: ToggleWorkAccessReq) => Promise<void>;
  deleteWork: (data: DeleteWorkReq) => Promise<void>;
  deleteWorksBulk: (data: string[]) => Promise<void>;
  clearActiveWork: () => void;

  // OnBoarding 에서 사용
  updateWorkBulkWithAui: (aui: string, data: UpdateWorkBulkReq) => Promise<void>;
}

type UseWorkListResult = UseWorkListStateResult & UseWorkListActionsResult;

export const useWorkListState = (): UseWorkListStateResult => {
  const workList = useWorkListStore((s) => s.workList);
  const workListType = useWorkListStore((s) => s.workListType);
  const listOwnerAui = useWorkListStore((s) => s.listOwnerAui);
  const listScope = useWorkListStore((s) => s.listScope);
  const status = useWorkListStore((s) => s.status);

  return { workList, workListType, listOwnerAui, listScope, status };
};

export const useWorkListActions = (): UseWorkListActionsResult => {
  const pageOwnerAui = usePageOwnerIdentityStore((s) => s.pageOwner?.aui);
  const setWorkList = useWorkListStore((s) => s.setWorkList);
  const setWorkListType = useWorkListStore((s) => s.setWorkListType);
  const beginWorkListFetch = useWorkListStore((s) => s.beginWorkListFetch);
  const setWorkListWithScope = useWorkListStore((s) => s.setWorkListWithScope);
  const setWorkListError = useWorkListStore((s) => s.setWorkListError);
  const addWorkList = useWorkListStore((s) => s.addWorkList);
  const updateAfterIsPublicToggle = useWorkListStore((s) => s.updateAfterIsPublicToggle);
  const setSimpleListWork = useImportStationWorkStore((s) => s.setSimpleList);
  const setExportSelectedDataList = useExportStationWorkStore((s) => s.setSelectedDataList);
  const setExportSimpleList = useExportStationWorkStore((s) => s.setSimpleList);
  const setActiveWork = useWorkViewStore((s) => s.setActiveWork);
  const setOnlyDownloadValue = useWorkViewStore((s) => s.setOnlyDownloadValue);
  const setOnlyAccessValue = useWorkViewStore((s) => s.setOnlyAccessValue);
  const setActiveWorkDetailList = useWorkViewStore((s) => s.setActiveWorkDetailList);
  const setActiveRelatedProjectList = useWorkViewStore((s) => s.setActiveRelatedProjectList);
  const afterDeleteActiveWork = useWorkViewStore((s) => s.afterDeleteActiveWork);
  const addMultiSelectedWork = useWorkViewStore((s) => s.addMultiSelectedWork);
  const clearActiveWork = useWorkViewStore((s) => s.clearActiveWork);
  const setUpdatedTempAlert = useTempAlertStore((s) => s.setUpdatedTempAlert);
  const setDeletedTempAlert = useTempAlertStore((s) => s.setDeletedTempAlert);
  const clearSelectedIndexingWorkList = useWorkIndexStore((s) => s.clearSelectedIndexingWorkList);
  const setPage = usePageStore((s) => s.setPage);
  const setModalPage = useModalPageStore((s) => s.setModalPage);
  const clearModal = useModalStore((s) => s.clearModal);
  const withApiHandler = useApiWrapper();

  const handleGetWorkSuccess = (response: WorkWithDetailAndRelatedProjectResponse) => {
    const data = normalizeWorkData(response.data);
    setActiveWork(data);
    setActiveWorkDetailList(data.workDetailList);
    setActiveRelatedProjectList(data.relatedProjectList);
  };

  const handleGetSimpleWorkPageForImportSuccess = (response: WorkSimplePageResponseForImport) => {
    const data = response.data;
    setModalPage(data);
    setSimpleListWork(data.content.map(normalizeWorkSimpleData));
  };

  const handleGetSimpleWorkPageForExportSuccess = (response: WorkSimplePageResponseForExport) => {
    const data = response.data;
    setModalPage(data);
    setExportSimpleList(data.content.map(normalizeWorkSimpleForExportData));
  };

  const handleGetWorkPageSuccess = (
    response: WorkPageResponse,
    ownerAui: string,
    scope: ResourceScope,
  ) => {
    const data = response.data;
    setPage(data);
    setWorkListWithScope(data.content.map(normalizeWorkData), ownerAui, scope);
  };

  const fetchWorkList = async (
    aui: string,
    scope: ResourceScope,
    metaData: MetadataForQuery,
  ) => {
    beginWorkListFetch(aui, scope);
    try {
      return await runCurrentArchiveRequest(
        aui,
        () => (scope === 'owner' ? getWorkListForOwner(aui, metaData) : getWorkList(aui, metaData)),
        (response) => handleGetWorkPageSuccess(response, aui, scope),
      );
    } catch (error) {
      setWorkListError();
      throw error;
    }
  };

  const handleUpdateWorkSuccess = (response: WorkResponse) => {
    const data = normalizeWorkData(response.data);
    const { workList } = useWorkListStore.getState();
    const newWorkList = workList.map((each) => each.id === data.id ? data : each);
    setUpdatedTempAlert();
    setActiveWork(data);
    setWorkList(newWorkList);
  };

  const handleToggleWorkAllowDownloadSuccess = (response: WorkResponse) => {
    const data = response.data;
    setUpdatedTempAlert();
    setOnlyDownloadValue(data.uploadFile.allowDownload);
  };

  const handleToggleWorkAccessSuccess = (response: ToggleWorkAccessResponse) => {
    const { id, isPublic } = response.data;
    setUpdatedTempAlert();
    setOnlyAccessValue(isPublic);
    updateAfterIsPublicToggle(id, isPublic);
  };

  const handleDeleteWorkSuccess = (_response: DeleteResponse) => {
    console.log("deleted well");
    afterDeleteActiveWork();
    useEditModeStore.getState().setIsEditMode(false);
    setDeletedTempAlert();
  };

  const handleUpdateWorkBulkSuccess = (response: WorkListResponse) => {
    const data = response.data.map(normalizeWorkData);

    setUpdatedTempAlert();
    addWorkList(data);
    clearModal();
  };

  const handleUpdateWorkBulkInfoSuccess = (response: WorkListResponse) => {
    const data = response.data.map(normalizeWorkData);
    setUpdatedTempAlert();
    const byId = new Map(data.map((w) => [w.id, w]));
    const { workList } = useWorkListStore.getState();
    setWorkList(workList.map((w) => byId.get(w.id) ?? w));
    // 편집 종료·단일 전환 후 도착하면 multi를 다시 채우지 않음
    if (useWorkViewStore.getState().multiSelectedWork.length > 0) {
      addMultiSelectedWork(data);
    }
    useWorkViewStore.getState().setMultiHasChanged(false);
  };

  const handleGetSelectedWorkListForOwnerSuccess = (response: WorkListResponse) => {
    const data = response.data.map(normalizeWorkData);
    const store = useWorkViewStore.getState();
    // 편집 종료 후 늦게 도착한 응답이 multi를 복구하지 않도록
    if (!useEditModeStore.getState().isEditMode || store.multiSelectedWork.length === 0) {
      return;
    }
    addMultiSelectedWork(data);
  };

  const handleGetSelectedWorkListForExportSuccess = (response: WorkListResponse) => {
    const data = response.data.map(normalizeWorkData);
    setExportSelectedDataList(data.map(convertWorkToSelectedData));
  };

  const handleReorderWorksSuccess = (_response: WorkListResponse) => {
    setUpdatedTempAlert();
    clearSelectedIndexingWorkList();
  };

  const handleUpdateWorkListTypeSuccess = (response: WorkListTypeResponse) => {
    setWorkListType(response.data.workListType);
  };

  const handleGetManualOrderWorkListSuccess = (response: WorkManualOrderListResponse) => {
    const rows: IndexOrderWorkData[] = response.data.map((dto) => ({
      id: String(dto.id),
      title: dto.title,
      originUrl: dto.originUrl ?? '',
      artist: dto.artist ?? null,
      index: 0,
    }));
    useWorkIndexStore.getState().replaceSelectedIndexingWorkList(rows);
  };

  const reloadCurrentWorkListPage = () => {
    const { page } = usePageStore.getState();
    const { pageSize, sortData, filterData } = useWorkListStore.getState();
    void getWorkListOwnerHandler({ page: page.page, size: pageSize, sortData, filterData });
  };

  const handleWorkRequest = async (
    action: 
    'get' | 
    'get_for_owner' | 
    'get_page' | 
    'get_page_for_owner' | 
    'get_simple_page_for_import' | 
    'get_simple_page_for_export' | 
    'update' | 
    'update_bulk' |
    'update_bulk_info' |
    'reorder' |
    'update_work_list_type' |
    'delete' | 
    'delete_bulk' |
    'toggle_allowDownload' | 
    'toggle_access' | 
    'get_selected_work_list_for_multi' |
    'get_selected_work_list_for_export' |
    'get_manual_order_list_for_owner',
    data?: UpdateWorkReq | DeleteWorkReq | UpdateWorkBulkReq | UpdateWorkBulkInfoReq | UpdateReorderListReq | UpdateWorkListTypeReq | ToggleWorkAccessReq | string | string[],
    metaData?: MetadataForQuery
  ) => {
    if (!pageOwnerAui) return;
    const aui = pageOwnerAui;
    const apiFunction = async () => {
      switch (action) {
        case 'delete':
          await runCurrentArchiveRequest(
            aui,
            () => deleteWork(aui, data as DeleteWorkReq),
            (response) => {
              handleDeleteWorkSuccess(response);
              reloadCurrentWorkListPage();
            },
          );
          break;
        case 'delete_bulk':
          await runCurrentArchiveRequest(
            aui,
            () => deleteWorksBulk(aui, data as string[]),
            (response) => {
              handleDeleteWorkSuccess(response);
              reloadCurrentWorkListPage();
            },
          );
          break;
        case 'update':
          await runCurrentArchiveRequest(
            aui,
            () => updateWork(aui, data as UpdateWorkReq),
            handleUpdateWorkSuccess,
          );
          break;
        case 'update_bulk':
          await runCurrentArchiveRequest(
            aui,
            () => updateWorkBulk(aui, data as UpdateWorkBulkReq),
            handleUpdateWorkBulkSuccess,
          );
          break;
        case 'update_bulk_info':
          await runCurrentArchiveRequest(
            aui,
            () => updateWorkBulkInfo(aui, data as UpdateWorkBulkInfoReq),
            handleUpdateWorkBulkInfoSuccess,
          );
          break;
        case 'reorder':
          await runCurrentArchiveRequest(
            aui,
            () => reorderWorks(aui, data as UpdateReorderListReq),
            handleReorderWorksSuccess,
          );
          break;
        case 'update_work_list_type':
          await runCurrentArchiveRequest(
            aui,
            () => updateWorkListType(aui, data as UpdateWorkListTypeReq),
            handleUpdateWorkListTypeSuccess,
          );
          break;
        case 'toggle_allowDownload':
          await runCurrentArchiveRequest(
            aui,
            () => toggleWorkAllowDownload(aui, data as UpdateWorkReq),
            handleToggleWorkAllowDownloadSuccess,
          );
          break;
        case 'toggle_access':
          await runCurrentArchiveRequest(
            aui,
            () => toggleWorkAccess(aui, data as ToggleWorkAccessReq),
            handleToggleWorkAccessSuccess,
          );
          break;
        case 'get_simple_page_for_import':
          await runCurrentArchiveRequest(
            aui,
            () => getSimpleWorkListForImportForOwner(aui, metaData!),
            handleGetSimpleWorkPageForImportSuccess,
          );
          break;
        case 'get_simple_page_for_export':
          await runCurrentArchiveRequest(
            aui,
            () => getSimpleWorkListForExportForOwner(aui, metaData!),
            handleGetSimpleWorkPageForExportSuccess,
          );
          break;
        case 'get_page':
          await fetchWorkList(aui, 'public', metaData!);
          break;
        case 'get_page_for_owner':
          await fetchWorkList(aui, 'owner', metaData!);
          break;
        case 'get_for_owner':
          await runCurrentArchiveRequest(
            aui,
            () => getWorkForOwner(data as string),
            handleGetWorkSuccess,
          );
          break;
        case 'get_selected_work_list_for_export':
          await runCurrentArchiveRequest(
            aui,
            () => getSelectedWorkListForOwner(aui, data as string[]),
            handleGetSelectedWorkListForExportSuccess,
          );
          break;
        case 'get_selected_work_list_for_multi':
          await runCurrentArchiveRequest(
            aui,
            () => getSelectedWorkListForOwner(aui, data as string[]),
            handleGetSelectedWorkListForOwnerSuccess,
          );
          break;
        case 'get_manual_order_list_for_owner':
          await runCurrentArchiveRequest(
            aui,
            () => getManualOrderWorkListForOwner(aui),
            handleGetManualOrderWorkListSuccess,
          );
          break;
        case 'get':
        default:
          await runCurrentArchiveRequest(
            aui,
            () => getWork(data as string),
            handleGetWorkSuccess,
          );
          break;
      }
    };

    await withApiHandler(apiFunction, [aui, action, data]);
  };

  const handleWorkRequestWithAui = async (
    aui: string,
    action: 'update_bulk',
    data?: UpdateWorkBulkReq
  ) => {
    const apiFunction = async () => {
      switch (action) {
        case 'update_bulk':
        default:
          handleUpdateWorkBulkSuccess(await updateWorkBulk(aui, data as UpdateWorkBulkReq));
          break;
      }
    };

    await withApiHandler(apiFunction, [aui, action, data]);
  };


  const getWorkHandler = (workId: string) => handleWorkRequest('get', workId);
  const getWorkOwnerHandler = (workId: string) => handleWorkRequest('get_for_owner', workId);
  const getWorkListHandler = (metaData: MetadataForQuery) => handleWorkRequest('get_page', undefined, metaData);
  const getWorkListOwnerHandler = (metaData: MetadataForQuery) => handleWorkRequest('get_page_for_owner', undefined, metaData);
  const getSelectedWorkListForOwnerHandler = (workIds: string[]) => handleWorkRequest('get_selected_work_list_for_multi', workIds);
  const getManualOrderWorkListForOwnerHandler = () => handleWorkRequest('get_manual_order_list_for_owner');
  const getSelectedWorkListForExportHandler = (workIds: string[]) => handleWorkRequest('get_selected_work_list_for_export', workIds);
  const getSimpleWorkListForImportHandler = (metaData: MetadataForQuery) => handleWorkRequest('get_simple_page_for_import', undefined, metaData);
  const getSimpleWorkListForExportHandler = (metaData: MetadataForQuery) => handleWorkRequest('get_simple_page_for_export', undefined, metaData);
  const updateWorkHandler = (data: UpdateWorkReq) => handleWorkRequest('update', data);
  const updateWorkBulkHandler = (data: UpdateWorkBulkReq) => handleWorkRequest('update_bulk', data);
  const updateWorkBulkInfoHandler = (data: UpdateWorkBulkInfoReq) => handleWorkRequest('update_bulk_info', data);
  const reorderWorksHandler = (data: UpdateReorderListReq) => handleWorkRequest('reorder', data);
  const updateWorkListTypeHandler = (data: UpdateWorkListTypeReq) => handleWorkRequest('update_work_list_type', data);
  const updateWorkBulkWithAuiHandler = (aui: string, data: UpdateWorkBulkReq) => handleWorkRequestWithAui(aui, 'update_bulk', data);
  const toggleWorkAllowDownloadHandler = (data: UpdateWorkReq) => handleWorkRequest('toggle_allowDownload', data);
  const toggleWorkAccessHandler = (data: ToggleWorkAccessReq) => handleWorkRequest('toggle_access', data);
  const deleteWorkHandler = (data: DeleteWorkReq) => handleWorkRequest('delete', data);
  const deleteWorksBulkHandler = (data: string[]) => handleWorkRequest('delete_bulk', data);

  return {
    getWork: getWorkHandler,
    getWorkOwner: getWorkOwnerHandler,
    getWorkList: getWorkListHandler,
    getWorkListOwner: getWorkListOwnerHandler,
    getSelectedWorkListForExport: getSelectedWorkListForExportHandler,
    getSimpleWorkListForImport: getSimpleWorkListForImportHandler,
    getSimpleWorkListForExport: getSimpleWorkListForExportHandler,
    updateWork: updateWorkHandler,
    updateWorkBulk: updateWorkBulkHandler,
    updateWorkBulkInfo: updateWorkBulkInfoHandler,
    reorderWorks: reorderWorksHandler,
    updateWorkListType: updateWorkListTypeHandler,
    updateWorkBulkWithAui: updateWorkBulkWithAuiHandler,
    toggleWorkAllowDownload: toggleWorkAllowDownloadHandler,
    toggleWorkAccess: toggleWorkAccessHandler,
    deleteWork: deleteWorkHandler,
    deleteWorksBulk: deleteWorksBulkHandler,
    getSelectedWorkListForOwner: getSelectedWorkListForOwnerHandler,
    getManualOrderWorkListForOwner: getManualOrderWorkListForOwnerHandler,
    clearActiveWork
  };
};

/** 기존 호출부 호환: state + actions */
export const useWorkList = (): UseWorkListResult => ({
  ...useWorkListState(),
  ...useWorkListActions(),
});
